import { Component, computed, inject, linkedSignal, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { DatePipe, NgTemplateOutlet } from '@angular/common';
import { Router, RouterLink, UrlTree } from '@angular/router';
import { MatDrawer } from '@angular/material/sidenav';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { IAlert } from 'src/app/core/dto';
import { AlertDataService } from 'src/app/main/shared/services/alert-data.service';
import { TimeAgoPipe } from 'src/app/main/shared/pipes/basic.pipe';
import { EditRoles, UserHasRoles } from 'src/app/main/shared/services/store/user/selector';
import { PunchPatternPreviewComponent } from 'src/app/main/shared/components/punch-pattern-preview/punch-pattern-preview.component';
import {
  AlertGroup,
  AlertItem,
  AlertTier,
  buildSections,
  TIER_ORDER,
  tierCounts,
  validDate,
} from 'src/app/main/shared/utils/alert-grouping';
import { QuickPanelService } from '../quick-panel.service';

export type AlertFilter = AlertTier | 'all';

/** What a row shows under its subject (or, for a single alert, under its title). */
export type ItemMeta =
  { kind: 'text'; text: string } | { kind: 'scheduled'; when: Date } | { kind: 'unscheduled' };

export interface OrderRow {
  id: string;
  code: string;
  /** The server's short state ("Not scheduled", "Finishes 8:15 PM · Machine 4"). */
  state: string;
  /** The server's whole description, for the row's tooltip. */
  description: string;
  /** Not scheduled, so shown in danger. */
  unscheduled: boolean;
  link: UrlTree | null;
}

export interface ItemRow {
  id: string;
  alert: IAlert;
  subject: string;
  meta: ItemMeta;
  /** Null when the alert has nowhere to go (it had no link in the old panel either). */
  link: UrlTree | null;
  /** The punch pattern the subject previews on hover, as app-link-helper does. */
  patternId: string | null;
  tooltip: string;
  orders: OrderRow[];
}

export interface GroupRow {
  key: string;
  tier: AlertTier;
  type: string;
  count: number;
  expandable: boolean;
  /** Translation key of the group title, or null to use `title`. */
  titleKey: string | null;
  title: string;
  summary: string;
  more: number;
  ordersLate: number;
  items: ItemRow[];
  /** The only item of a group that doesn't expand: the row shows it and navigates to it. */
  single: ItemRow | null;
}

export interface SectionRow {
  tier: AlertTier;
  count: number;
  groups: GroupRow[];
}

export const TIER_LABEL_KEYS: Record<AlertFilter, string> = {
  all: 'alertsTierAll',
  critical: 'alertsTierCritical',
  warning: 'alertsTierWarnings',
  notice: 'alertsTierNotices',
};

const GROUP_TITLE_KEYS: Record<string, string> = {
  MachineOffline: 'alertGroupMachineOffline',
  TruckLate: 'alertGroupTruckLate',
  ScheduleSlip: 'alertGroupScheduleSlip',
  WarehouseTaskLate: 'alertGroupWarehouseTaskLate',
  IntegrationError: 'alertGroupIntegrationError',
  Licensing: 'alertGroupLicensing',
};

/** Types the old panel showed without a link, although some carry a (legacy) sref. */
const UNLINKED_TYPES = ['IntegrationError', 'ScheduleSlip', 'WarehouseTaskLate'];

/** Routes for a `ReferenceDocumentId`, the same ones app-link-helper links to. */
const DOCUMENT_ROUTES: Record<string, string> = {
  JobDetail: '/orders',
  Coil: '/inventory/coils',
  PunchPattern: '/punching/patterns',
  ToolingDef: '/tooling',
  Material: '/inventory/coil-types',
};

/** `PunchPattern/ABC` → ['PunchPattern', 'ABC']; ids may contain slashes themselves. */
export function splitDocumentId(documentId: string | undefined): [string, string] | null {
  const i = (documentId ?? '').indexOf('/');
  if (i < 0) return null;
  const id = documentId.slice(i + 1);
  if (!id || id === 'null' || id === '0' || id === 'NO COIL MATCH') return null;
  return [documentId.slice(0, i), id];
}

/** An OrderLate alert's OrdId, from `JobDetail/{ordId}` (or its id, `OrderLate/{ordId}`). */
export function ordIdOf(alert: IAlert): string | null {
  const doc = splitDocumentId(alert.referenceDocumentId);
  if (doc?.[0] === 'JobDetail') return doc[1];
  const fromId = splitDocumentId(alert.id);
  return fromId?.[0] === 'OrderLate' ? fromId[1] : null;
}

/** The group title for a type without a key: the first item's title with its subject removed. */
export function fallbackTitle(item: AlertItem<IAlert>): string {
  const title = (item.alert.title ?? '').trim();
  const subject = item.subject;
  if (!subject || subject === title) return title;
  const colon = title.indexOf(': ');
  if (colon >= 0 && title.slice(colon + 2).trim() === subject) return title.slice(0, colon).trim();
  const forIdx = title.lastIndexOf(' for ');
  if (forIdx >= 0 && title.slice(forIdx + 5).trim() === subject)
    return title.slice(0, forIdx).trim();
  return title;
}

export function metaOf(item: AlertItem<IAlert>): ItemMeta {
  const a = item.alert;
  switch (a.alertType) {
    case 'ScheduleSlip': {
      const when = validDate(a.expectedDate);
      return when ? { kind: 'scheduled', when } : { kind: 'text', text: a.description ?? '' };
    }
    case 'WarehouseTaskLate': {
      // The server no longer raises these for a task without a time; this guards against bad data.
      const when = validDate(a.effectiveDate);
      return when ? { kind: 'scheduled', when } : { kind: 'unscheduled' };
    }
    case 'SolutionError':
      return { kind: 'text', text: a.subTitle || a.description || '' };
    default:
      return { kind: 'text', text: a.description || a.subTitle || '' };
  }
}

/**
 * The alerts panel in the Steel shell (the bell in steel-header): alerts split into Critical,
 * Warnings and Notices, same-type alerts grouped into one expandable row, late orders nested under
 * their truck. Design: specs/designs/alerts-panel-steel-handoff/. Classic keeps fury-quickpanel.
 */
@Component({
  selector: 'app-steel-alerts-panel',
  templateUrl: './steel-alerts-panel.component.html',
  styleUrls: ['./steel-alerts-panel.component.scss'],
  imports: [
    DatePipe,
    NgTemplateOutlet,
    RouterLink,
    MatTooltipModule,
    TranslateModule,
    TimeAgoPipe,
    PunchPatternPreviewComponent,
  ],
})
export class SteelAlertsPanelComponent {
  private alertService = inject(AlertDataService);
  private quickPanelService = inject(QuickPanelService);
  private router = inject(Router);
  private drawer = inject(MatDrawer, { optional: true });
  // Acknowledging / closing an alert is an everyday action; links to other pages stay for everyone.
  readonly canAct = inject(Store).selectSignal(UserHasRoles(EditRoles.basic, false));

  readonly tierLabelKeys = TIER_LABEL_KEYS;
  readonly filters: AlertFilter[] = ['all', ...TIER_ORDER];

  private readonly alerts = toSignal(this.alertService.alerts$, { initialValue: [] as IAlert[] });
  private readonly sections = computed(() => buildSections(this.alerts()));
  readonly counts = computed(() => tierCounts(this.sections()));
  readonly rows = computed<SectionRow[]>(() =>
    this.sections().map((s) => ({
      tier: s.tier,
      count: s.count,
      groups: s.groups.map((g) => this.groupRow(g)),
    }))
  );

  /** Not persisted; back to All whenever the panel opens. */
  readonly filter = signal<AlertFilter>('all');
  readonly visibleRows = computed(() => {
    const f = this.filter();
    return f === 'all' ? this.rows() : this.rows().filter((s) => s.tier === f);
  });

  /** Group keys that have been expandable at some point this session. */
  private readonly seen = new Set<string>();

  /**
   * Open groups, for the session. A group seen expandable for the first time follows the default
   * (open when critical); every other group keeps whatever state it had.
   */
  readonly expanded = linkedSignal<SectionRow[], ReadonlySet<string>>({
    source: this.rows,
    computation: (sections, previous) => {
      const open = new Set(previous?.value ?? []);
      for (const s of sections) {
        for (const g of s.groups) {
          if (!g.expandable || this.seen.has(g.key)) continue;
          this.seen.add(g.key);
          if (s.tier === 'critical') open.add(g.key);
        }
      }
      return open;
    },
  });

  private readonly expandableKeys = computed(() =>
    this.rows().flatMap((s) => s.groups.filter((g) => g.expandable).map((g) => g.key))
  );
  readonly hasExpandable = computed(() => this.expandableKeys().length > 0);
  readonly anyOpen = computed(() => {
    const open = this.expanded();
    return this.expandableKeys().some((k) => open.has(k));
  });

  constructor() {
    this.drawer?.openedStart.pipe(takeUntilDestroyed()).subscribe(() => this.filter.set('all'));
  }

  isOpen(key: string): boolean {
    return this.expanded().has(key);
  }

  toggle(key: string): void {
    this.expanded.update((open) => {
      const next = new Set(open);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  }

  toggleAll(): void {
    this.expanded.set(this.anyOpen() ? new Set() : new Set(this.expandableKeys()));
  }

  listId(key: string): string {
    return 'ap-items-' + key.replace(/[^A-Za-z0-9_-]/g, '-');
  }

  close(): void {
    this.quickPanelService.onSetOpen(false);
  }

  /** Runs an alert action whose target is `post` (e.g. Acknowledge, Close). */
  postAction(url: string): void {
    if (!this.canAct()) {
      return;
    }
    this.alertService.postAlert(url).subscribe();
  }

  private groupRow(g: AlertGroup<IAlert>): GroupRow {
    const items = g.items.map((it) => this.itemRow(it));
    const titleKey =
      g.type === 'SolutionError'
        ? g.tier === 'critical'
          ? 'alertGroupSolutionFailed'
          : 'alertGroupSolutionPending'
        : (GROUP_TITLE_KEYS[g.type] ?? null);
    return {
      key: g.key,
      tier: g.tier,
      type: g.type,
      count: g.count,
      expandable: g.expandable,
      titleKey,
      title: titleKey ? '' : fallbackTitle(g.items[0]),
      summary: g.summarySubjects.join(', '),
      more: g.summaryMore,
      ordersLate: g.childCount,
      items,
      single: g.expandable ? null : items[0],
    };
  }

  private itemRow(it: AlertItem<IAlert>): ItemRow {
    const a = it.alert;
    const doc = splitDocumentId(a.referenceDocumentId);
    return {
      id: a.id,
      alert: a,
      subject: it.subject,
      meta: metaOf(it),
      link: this.linkOf(a),
      patternId: a.alertType !== 'OrderLate' && doc?.[0] === 'PunchPattern' ? doc[1] : null,
      tooltip:
        a.alertType === 'TruckLate' || a.alertType === 'OrderLate' ? '' : (a.description ?? ''),
      orders: it.children.map((c) => this.orderRow(c)),
    };
  }

  private orderRow(c: AlertItem<IAlert>): OrderRow {
    const a = c.alert;
    const ordId = ordIdOf(a);
    const description = (a.description ?? '').trim();
    return {
      id: a.id,
      code: c.subject,
      state: a.subTitle?.trim() || description,
      description,
      // TruckScheduleAlerts keeps the red icon only for orders with no projected finish.
      unscheduled: a.iconColor === 'red-fg',
      link: ordId ? this.router.createUrlTree(['/orders', ordId]) : null,
    };
  }

  /** Where the row goes; every alert keeps the destination it had in the old panel. */
  private linkOf(a: IAlert): UrlTree | null {
    switch (a.alertType) {
      case 'Licensing':
        return this.router.createUrlTree(['/settings/licensing']);
      case 'OrderLate': {
        const ordId = ordIdOf(a);
        return ordId ? this.router.createUrlTree(['/orders', ordId]) : this.srefLink(a.sref);
      }
      case 'TruckLate':
        return this.srefLink(a.sref);
      default: {
        if (UNLINKED_TYPES.includes(a.alertType)) return null;
        const doc = splitDocumentId(a.referenceDocumentId);
        const route = doc && DOCUMENT_ROUTES[doc[0]];
        return route ? this.router.createUrlTree([route, doc[1]]) : this.srefLink(a.sref);
      }
    }
  }

  /**
   * App routes only. Some server alerts still carry AngularJS state names ("app.agent-settings"),
   * which the old panel navigated to and the router redirected to the dashboard.
   */
  private srefLink(sref: string | undefined): UrlTree | null {
    return sref?.startsWith('/') ? this.router.parseUrl(sref) : null;
  }
}
